import { useEffect, useState } from 'react';
import { fileUrl, forgetFile } from '@/core/files';
import { initialsOf } from '@/core/avatarImage';
import { cn } from './ui';

/* =============================================================================
 * Somebody's face, or their initials
 * -----------------------------------------------------------------------------
 * The initials are the ordinary case. Most people will never set a picture, and
 * a component that treats that as a failure — a broken image, a grey silhouette
 * — makes every list look like something went wrong. So the initials are the
 * design, and a photograph is what replaces them when there is one.
 *
 * The colour is derived from the name rather than chosen. On a site where half
 * the workforce shares three surnames, a wall of identical circles is no help
 * at all; the same person being the same colour on every screen is.
 *
 * SIGNED URLS
 * -----------------------------------------------------------------------------
 * Files live on ImageKit as private objects, reachable only through a URL that
 * file-access signs for a few minutes after RLS has agreed the caller may see
 * it. So an avatar is a network round trip before it is an image, and a table of
 * two hundred is two hundred of them.
 *
 * This component used to hold its own map of those to prevent that. It has been
 * moved into fileUrl, where every other kind of file gets the benefit too — and,
 * more to the point, where an entry expires. The map here did not: it kept
 * handing out ten-minute signatures for the life of the page, so on a kiosk left
 * open the faces quietly became initials after ten minutes and stayed that way.
 * ========================================================================== */

/** Kept as a name of its own, because what callers mean is "this face changed". */
export const forgetAvatar = forgetFile;

/* Ten minutes. Long enough that a list stays visible while somebody reads it,
   short enough to be worth signing at all. */
const AVATAR_SECONDS = 600;

/* Eight, from the palette the rest of the app already uses. Fixed rather than
   generated: a hash producing arbitrary hues lands on colours that white text
   cannot be read against, and it does it unpredictably. */
const TONES = [
  'bg-brand/15 text-brand',
  'bg-info-subtle text-info',
  'bg-ok-subtle text-ok',
  'bg-warn-subtle text-warn',
  'bg-accent/15 text-accent',
  'bg-danger-subtle text-danger',
  'bg-surface-3 text-muted',
  'bg-brand/25 text-brand',
];

/* Deterministic, and stable across sessions and devices — the same person is
   the same colour on the site foreman's phone and in the payroll office. */
function toneFor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return TONES[Math.abs(hash) % TONES.length] ?? TONES[0]!;
}

const SIZES = {
  xs: 'w-6 h-6 text-[9px]',
  sm: 'w-8 h-8 text-[11px]',
  md: 'w-10 h-10 text-[13px]',
  lg: 'w-16 h-16 text-[20px]',
  xl: 'w-24 h-24 text-[30px]',
} as const;

export function Avatar({
  name, path, size = 'md', className,
}: {
  name: string | null | undefined;
  /** ImageKit path, or null for the ordinary case. */
  path?: string | null;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!path) {
      setUrl(null);
      return;
    }

    let alive = true;
    void fileUrl(path, AVATAR_SECONDS)
      .then((signed) => { if (alive) setUrl(signed); })
      /* Silent on purpose. A picture that will not load falls back to initials,
         which is a complete and correct thing to show — an error message beside
         somebody's name in a table is not. */
      .catch(() => { if (alive) setUrl(null); });

    return () => { alive = false; };
  }, [path]);

  const initials = initialsOf(name);

  return (
    <span
      className={cn(
        'shrink-0 rounded-full overflow-hidden grid place-items-center',
        'font-semibold select-none',
        SIZES[size],
        !url && toneFor(name ?? ''),
        className,
      )}
      /* The name, for anybody using a screen reader and for anybody hovering a
         table of faces trying to find one person. */
      title={name ?? undefined}
    >
      {url ? (
        <img
          src={url}
          alt={name ?? ''}
          className="w-full h-full object-cover"
          /* Already square and already small, so nothing here is deferred —
             but a browser that fails to fetch it must not leave a broken-image
             glyph where a face should be. */
          onError={() => setUrl(null)}
        />
      ) : (
        <span aria-hidden>{initials}</span>
      )}
    </span>
  );
}
